/**
 * Logo Optimization Service
 *
 * Client-side image optimization for logos using Canvas API.
 * Creates optimized variants for invoice rendering and UI preview.
 *
 * Target sizes:
 * - Invoice: 1200px width (crisp at 300 DPI print on A4)
 * - Preview: 400px width (settings page, retina displays)
 */

import { logger } from '@/lib/logger';

// ============================================================================
// Constants
// ============================================================================

/** Target width for invoice logo (crisp at 300 DPI on A4) */
export const INVOICE_LOGO_WIDTH = 1200;

/** Target width for UI preview */
export const PREVIEW_LOGO_WIDTH = 400;

/** Minimum width for invoice logo (upscale small logos to ensure print quality) */
export const MIN_INVOICE_LOGO_WIDTH = 400;

/** Quality for JPEG/WebP compression (0-1) */
export const COMPRESSION_QUALITY = 0.9;

/** Maximum output file size in bytes (500KB) */
export const MAX_OUTPUT_SIZE = 500 * 1024;

// ============================================================================
// Types
// ============================================================================

export interface LogoVariant {
  blob: Blob;
  width: number;
  height: number;
  format: 'image/png' | 'image/webp';
}

export interface OptimizedLogo {
  /** Optimized logo for invoice rendering (800px width) */
  invoice: LogoVariant;
  /** Smaller preview for settings UI (200px width) */
  preview: LogoVariant;
  /** Original dimensions */
  originalWidth: number;
  originalHeight: number;
  /** Whether the original had transparency */
  hasAlpha: boolean;
}

// ============================================================================
// Optimization Functions
// ============================================================================

/**
 * Optimize a logo file for invoice and preview usage
 *
 * - Resizes to target widths while preserving aspect ratio
 * - Preserves transparency (PNG) or converts to WebP (no alpha)
 * - Compresses to reduce file size
 */
export async function optimizeLogo(file: File): Promise<OptimizedLogo> {
  logger.log('[logoOptimization] Starting optimization for:', file.name, file.type);

  // Load image
  const img = await loadImage(file);
  const { naturalWidth, naturalHeight } = img;

  logger.log('[logoOptimization] Original dimensions:', naturalWidth, 'x', naturalHeight);

  // Detect transparency
  const hasAlpha = await detectTransparency(img);
  logger.log('[logoOptimization] Has transparency:', hasAlpha);

  // Choose output format based on transparency
  const format: 'image/png' | 'image/webp' = hasAlpha ? 'image/png' : 'image/webp';

  // Create invoice variant
  // - Cap at INVOICE_LOGO_WIDTH (1200px) for large images
  // - Upscale to MIN_INVOICE_LOGO_WIDTH (400px) for small images to ensure print quality
  // - Keep original size if between min and max
  let invoiceWidth: number;
  if (naturalWidth > INVOICE_LOGO_WIDTH) {
    invoiceWidth = INVOICE_LOGO_WIDTH; // Downscale large images
  } else if (naturalWidth < MIN_INVOICE_LOGO_WIDTH) {
    invoiceWidth = MIN_INVOICE_LOGO_WIDTH; // Upscale small images
    logger.log('[logoOptimization] Upscaling small logo from', naturalWidth, 'to', invoiceWidth);
  } else {
    invoiceWidth = naturalWidth; // Keep original size
  }
  const invoiceVariant = await resizeImage(img, invoiceWidth, format, hasAlpha);

  // Create preview variant (cap at PREVIEW_LOGO_WIDTH, no upscaling needed for preview)
  const previewWidth = Math.min(PREVIEW_LOGO_WIDTH, naturalWidth);
  const previewVariant = await resizeImage(img, previewWidth, format, hasAlpha);

  logger.log(
    '[logoOptimization] Invoice variant:',
    invoiceVariant.width,
    'x',
    invoiceVariant.height,
    'size:',
    formatBytes(invoiceVariant.blob.size)
  );
  logger.log(
    '[logoOptimization] Preview variant:',
    previewVariant.width,
    'x',
    previewVariant.height,
    'size:',
    formatBytes(previewVariant.blob.size)
  );

  return {
    invoice: invoiceVariant,
    preview: previewVariant,
    originalWidth: naturalWidth,
    originalHeight: naturalHeight,
    hasAlpha,
  };
}

/**
 * Load an image from a File
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Detect if image has transparency
 */
async function detectTransparency(img: HTMLImageElement): Promise<boolean> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return false;

  // Use smaller size for analysis
  const maxSize = 100;
  const scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
  canvas.width = Math.floor(img.naturalWidth * scale);
  canvas.height = Math.floor(img.naturalHeight * scale);

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  try {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Check alpha channel
    for (let i = 3; i < data.length; i += 4) {
      const alpha = data[i];
      if (alpha !== undefined && alpha < 250) {
        return true; // Found transparent pixel
      }
    }
  } catch {
    // CORS error - assume no transparency
    logger.warn('[logoOptimization] Could not analyze transparency (CORS)');
  }

  return false;
}

/**
 * Resize image to target width while preserving aspect ratio
 */
async function resizeImage(
  img: HTMLImageElement,
  targetWidth: number,
  format: 'image/png' | 'image/webp',
  preserveAlpha: boolean
): Promise<LogoVariant> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not create canvas context');
  }

  // Calculate dimensions
  const scale = targetWidth / img.naturalWidth;
  const targetHeight = Math.round(img.naturalHeight * scale);

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  // Set background for non-transparent images
  if (!preserveAlpha) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Use high-quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw image
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  // Convert to blob with compression
  const blob = await canvasToBlob(canvas, format, COMPRESSION_QUALITY);

  // If blob is too large and we're using WebP, try lower quality
  if (blob.size > MAX_OUTPUT_SIZE && format === 'image/webp') {
    logger.log('[logoOptimization] Output too large, reducing quality');
    const reducedBlob = await canvasToBlob(canvas, format, 0.8);
    return {
      blob: reducedBlob,
      width: targetWidth,
      height: targetHeight,
      format,
    };
  }

  return {
    blob,
    width: targetWidth,
    height: targetHeight,
    format,
  };
}

/**
 * Convert canvas to blob with specified format and quality
 */
function canvasToBlob(canvas: HTMLCanvasElement, format: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      format,
      quality
    );
  });
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Create a File from a Blob with proper naming
 */
export function blobToFile(blob: Blob, baseName: string, variant: 'invoice' | 'preview'): File {
  const ext = blob.type === 'image/png' ? 'png' : 'webp';
  const fileName = `${baseName}-${variant}.${ext}`;
  return new File([blob], fileName, { type: blob.type });
}
